import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { isAxiosError } from "axios";
import type { AxiosInstance } from "axios";
import { useCustomerAppConfig } from "undercarriage";

import { bookkeepingWaitlistApiPath } from "./constants";
import { parseWaitlistEntry } from "./parse-waitlist-entry";
import type { WaitlistEntryCreatePayload, WaitlistEntryOut } from "./types";

export const BOOKKEEPING_WAITLIST_QUERY_KEY = ["bookkeeping-waitlist"] as const;

export const getBookkeepingWaitlistQueryOptions = ({
  customerDomain,
  client,
  enabled = true,
}: {
  customerDomain: string | undefined;
  client: AxiosInstance;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: [...BOOKKEEPING_WAITLIST_QUERY_KEY, customerDomain] as const,
    enabled: Boolean(customerDomain) && enabled,
    retry: false,
    queryFn: async (): Promise<WaitlistEntryOut | null> => {
      if (!customerDomain) {
        return null;
      }
      const url = bookkeepingWaitlistApiPath(customerDomain);
      try {
        const response = await client.get<unknown>(url);
        if (response.status === 204) {
          return null;
        }
        return parseWaitlistEntry(response.data);
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });

export const useBookkeepingWaitlistQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const client = useAxios();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  return useQuery(getBookkeepingWaitlistQueryOptions({ customerDomain, client, enabled }));
};

export const useSubmitBookkeepingWaitlistMutation = () => {
  const client = useAxios();
  const queryClient = useQueryClient();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  return useMutation({
    mutationKey: [...BOOKKEEPING_WAITLIST_QUERY_KEY, "submit", customerDomain],
    mutationFn: async (payload: WaitlistEntryCreatePayload) => {
      if (!customerDomain) {
        throw new Error("Missing customer domain");
      }
      const url = bookkeepingWaitlistApiPath(customerDomain);
      await client.post(url, payload);
    },
    onSuccess: async () => {
      if (customerDomain) {
        await queryClient.invalidateQueries({ queryKey: BOOKKEEPING_WAITLIST_QUERY_KEY });
      }
    },
  });
};
